import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { resolveMediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, FileText, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticlesList() {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/articles/admin/all`, { headers });
      setArticles(data);
    } catch {
      toast.error('Failed to load articles');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const togglePublished = async (a) => {
    try {
      await axios.put(`${API}/articles/${a.id}`, { is_published: !a.is_published }, { headers });
      load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete article "${a.title_en}"?`)) return;
    try {
      await axios.delete(`${API}/articles/${a.id}`, { headers });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="articles-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Blog Articles</h1>
          <p className="text-gray-500 text-sm mt-1">SEO-friendly articles. Hindi versions are auto-translated by AI on save.</p>
        </div>
        <Link to="/dashboard/articles/new">
          <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-article-btn">
            <Plus className="h-4 w-4 mr-2" /> New Article
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>
      ) : (
        <div className="grid gap-4">
          {articles.map((a) => (
            <Card key={a.id} data-testid={`article-row-${a.slug}`}>
              <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                {a.cover_image ? (
                  <img src={resolveMediaUrl(a.cover_image)} alt="" className="w-24 h-16 object-cover rounded-lg" />
                ) : (
                  <div className="w-24 h-16 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-brand-orange/60">
                    <FileText className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-heading font-semibold text-gray-900 truncate">{a.title_en}</h3>
                    <span className="text-xs text-gray-500 font-mono">/{a.slug}</span>
                    {!a.is_published && <Badge variant="outline" className="text-xs">Draft</Badge>}
                  </div>
                  {a.title_hi && <p className="text-sm text-gray-600 truncate">{a.title_hi}</p>}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(a.tags || []).slice(0, 4).map((tg) => (
                      <Badge key={tg} variant="outline" className="text-xs">#{tg}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Published</span>
                    <Switch checked={a.is_published} onCheckedChange={() => togglePublished(a)} />
                  </div>
                  <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline"><ExternalLink className="h-4 w-4" /></Button>
                  </a>
                  <Link to={`/dashboard/articles/${a.id}/edit`}>
                    <Button size="sm" variant="outline"><Edit className="h-4 w-4" /></Button>
                  </Link>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(a)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!articles.length && (
            <Card><CardContent className="p-10 text-center text-gray-500">No articles yet. Create your first SEO blog post!</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
